import type { ControlState } from '../controls/types';
import type { PointerLockStatus } from '../controls/inputAdapter';
import type { QualityTier } from '../render/types';
import type { HudState } from '../ui/types';
import { nextTier } from '../render/quality';

/* 'awaitingPainter' exists because O away from the painter view first flies there (PLAN §1); the
   overlay only shows once the painter pose is reached. */
export type OverlayPhase = 'hidden' | 'awaitingPainter' | 'visible';

export interface UiState {
  readonly tier: QualityTier;
  readonly helpOpen: boolean;
  readonly overlay: OverlayPhase;
  readonly minimapVisible: boolean;
  /** The visitor's place and view as numbers in the HUD: for saying where something is. */
  readonly coordsVisible: boolean;
}

export type UiEvent =
  | 'toggleHelp'
  | 'dismissHelp'
  | 'toggleOverlay'
  | 'cycleQuality'
  | 'toggleMinimap'
  | 'toggleCoords';

export type ControlView = Pick<ControlState, 'mode' | 'atPainterView' | 'transition'>;

export interface UiStep {
  readonly state: UiState;
  readonly requestPainterView: boolean;
}

export type UiEffect =
  | { readonly kind: 'help'; readonly open: boolean }
  | { readonly kind: 'input'; readonly enabled: boolean }
  | { readonly kind: 'overlay'; readonly visible: boolean }
  | { readonly kind: 'minimap'; readonly visible: boolean }
  | { readonly kind: 'quality'; readonly tier: QualityTier };

export function createUiState(tier: QualityTier): UiState {
  return {
    tier,
    helpOpen: false,
    overlay: 'hidden',
    minimapVisible: true,
    coordsVisible: false,
  };
}

function headingToPainter(control: ControlView): boolean {
  return control.mode === 'transition' && control.transition?.endsAtPainterView === true;
}

function withHelp(state: UiState, helpOpen: boolean): UiState {
  return state.helpOpen === helpOpen ? state : { ...state, helpOpen };
}

function toggleOverlay(state: UiState, control: ControlView): UiStep {
  if (state.overlay !== 'hidden') {
    return { state: { ...state, overlay: 'hidden' }, requestPainterView: false };
  }
  if (control.atPainterView) {
    return { state: { ...state, overlay: 'visible' }, requestPainterView: false };
  }
  return {
    state: { ...state, overlay: 'awaitingPainter' },
    requestPainterView: !headingToPainter(control),
  };
}

export function reduceUi(state: UiState, event: UiEvent, control: ControlView): UiStep {
  switch (event) {
    case 'toggleHelp':
      return { state: withHelp(state, !state.helpOpen), requestPainterView: false };
    case 'dismissHelp':
      return { state: withHelp(state, false), requestPainterView: false };
    case 'toggleOverlay':
      return toggleOverlay(state, control);
    case 'cycleQuality':
      return { state: { ...state, tier: nextTier(state.tier) }, requestPainterView: false };
    case 'toggleMinimap':
      return {
        state: { ...state, minimapVisible: !state.minimapVisible },
        requestPainterView: false,
      };
    case 'toggleCoords':
      return {
        state: { ...state, coordsVisible: !state.coordsVisible },
        requestPainterView: false,
      };
  }
}

export function settleOverlay(state: UiState, control: ControlView): UiState {
  if (state.overlay === 'visible' && !control.atPainterView) {
    return { ...state, overlay: 'hidden' };
  }
  if (state.overlay === 'awaitingPainter' && control.atPainterView) {
    return { ...state, overlay: 'visible' };
  }
  if (state.overlay === 'awaitingPainter' && !headingToPainter(control)) {
    return { ...state, overlay: 'hidden' };
  }
  return state;
}

/* Help visibility and input enabling are derived together from one field, so no close path can
   leave the input adapter disabled. */
export function uiEffects(prev: UiState, next: UiState): readonly UiEffect[] {
  const effects: UiEffect[] = [];
  if (prev.helpOpen !== next.helpOpen) {
    effects.push({ kind: 'help', open: next.helpOpen }, { kind: 'input', enabled: !next.helpOpen });
  }
  const wasVisible = prev.overlay === 'visible';
  const isVisible = next.overlay === 'visible';
  if (wasVisible !== isVisible) {
    effects.push({ kind: 'overlay', visible: isVisible });
  }
  if (prev.tier !== next.tier) {
    effects.push({ kind: 'quality', tier: next.tier });
  }
  if (prev.minimapVisible !== next.minimapVisible) {
    effects.push({ kind: 'minimap', visible: next.minimapVisible });
  }
  return effects;
}

export function createHudState(
  control: Pick<ControlState, 'mode' | 'atPainterView' | 'flySpeed' | 'pose'>,
  ui: UiState,
  pointerLock: PointerLockStatus
): HudState {
  return {
    mode: control.mode,
    atPainterView: control.atPainterView,
    flySpeed: control.flySpeed,
    tier: ui.tier,
    overlayVisible: ui.overlay === 'visible',
    helpOpen: ui.helpOpen,
    pointerLock,
    coords: ui.coordsVisible ? control.pose : null,
  };
}
